import React from 'react';
import { Box, Card, CardContent, Typography, Alert, Chip, Stack, Divider } from '@mui/material';

import type { CustomStep, CustomStepType } from '@shared/patient-intake-status';

import { NoteDisplay, StatusIcon } from '../../../components';
import { CurrentStatusViewer } from './CurrentStatusViewer.tsx';

const throwNever = (value: never): never => {
  throw new Error(`Unhandled value: ${value}`);
};

const getCustomStepTypeDisplayName = (stepType: CustomStepType): string => {
  switch (stepType) {
    case 'DOCUMENT_UPLOAD':
      return 'Document Upload';
    case 'MESSAGE':
      return 'Message';
    case 'DISCHARGE_LETTER_UPLOAD':
      return 'Discharge Letter Upload';
    default:
      return throwNever(stepType);
  }
};

type CustomStepViewerProps = {
  step: CustomStep[CustomStepType];
  healthCategoryId: string;
};

export const CustomStepViewer: React.FC<CustomStepViewerProps> = ({ step, healthCategoryId }) => {
  if (!step) {
    return <Alert severity="warning">Custom step data not found for category "{healthCategoryId}".</Alert>;
  }

  const { currentStatus } = step;

  if (!currentStatus) {
    return null;
  }

  const { actionedAt, actionedBy, note } = currentStatus;

  return (
    <Card variant="outlined">
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '0.5rem !important' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="h6">{step.title}</Typography>
          <Chip label={getCustomStepTypeDisplayName(step.type)} size="small" variant="outlined" />
        </Stack>

        <Typography variant="body2" sx={{ borderLeft: '4px solid', borderColor: 'divider', pl: 1 }}>
          {step.description}
        </Typography>

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            {!!actionedAt && <Typography variant="body2">Updated on {actionedAt.toDate().toLocaleString()}</Typography>}
            <StatusIcon status={currentStatus.status} stepIdentifier={step.type} />
          </Stack>
          {!!actionedBy && (
            <Typography variant="body2">
              By {actionedBy.name || actionedBy.email} ({actionedBy.userType})
            </Typography>
          )}
        </Box>

        <NoteDisplay stepId={step.id} noteConfig={note} />

        <Divider sx={{ my: 1, '&:last-child': { display: 'none' } }} />

        <CurrentStatusViewer step={step} />
      </CardContent>
    </Card>
  );
};
